import React, { useState } from "react";
import { Card, Flex, Text, Button, Badge } from "@radix-ui/themes";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { AuthBrevoDialog } from "./AuthBrevoDialog";
import { BrevoChoiceDialog } from "./BrevoChoiceDialog";
import { useValidateBrevo, useGetBrevoStatus } from "hooks";
import { PermissionAlertDialog } from "components";
import { useToast } from "../../../context/ToastContext";

export function BrowseBrevoCard({ userProfile }) {
  const navigate = useNavigate();
  const { setToastMessage } = useToast();
  const queryClient = useQueryClient();
  const [apiKey, setApiKey] = useState("");
  const [isApiKeyValid, setIsApiKeyValid] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [progressValue, setProgressValue] = useState(0);
  const [openDialog, setOpenDialog] = useState(false);
  const [showPermissionAlert, setShowPermissionAlert] = useState(false);
  const [choiceDialogOpen, setChoiceDialogOpen] = useState(false);
  const [pendingChoiceData, setPendingChoiceData] = useState(null);
  const validateBrevo = useValidateBrevo();
  const { data: brevoStatusData, isLoading: isStatusLoading } = useGetBrevoStatus(userProfile.organisation_id);

  const isBrevoActive = () => {
    return brevoStatusData?.data?.is_active === true;
  };

  const handleOpenChange = (isOpen) => {
    setOpenDialog(isOpen);
    if (!isOpen) {
      handleCancelAuthDialog();
    }
  };

  const handleConnectClick = () => {
    // Check if user is owner
    const isOwner = userProfile.user_role === "Owner";
    
    if (isOwner) {
      handleOpenChange(true);
    } else {
      setShowPermissionAlert(true);
    }
  };

  const handleCancelAuthDialog = () => {
    setApiKey("");
    setIsApiKeyValid(true);
  };

  const handleApiKeyChange = (event) => {
    setApiKey(event.target.value);
    setIsApiKeyValid(true);
  };

  const handleSave = async (choice = null) => {
    let isValid = true;

    if (!apiKey) {
      setIsApiKeyValid(false);
      isValid = false;
    }

    if (!isValid) return;

    setIsLoading(true);
    setProgressValue(10);

    try {
      const payload = {
        api_key: apiKey,
        organisation_id: userProfile.organisation_id,
      };

      if (choice) payload.action_choice = choice;

      const result = await validateBrevo.mutateAsync(payload);

      setProgressValue(50);

      const status = result?.data?.status;

      if (status === "pending_user_choice") {
        setPendingChoiceData(result.data);
        setChoiceDialogOpen(true);
        return;
      }

      if (status === "success" || status === "restored" || status === "resumed") {
        queryClient.invalidateQueries(["brevo-status"]);
        navigate("/brevo-setup", {
          state: {
            lists: result.data.lists,
            orgId: userProfile.organisation_id,
          },
        });
        return;
      }

      // Only treat these as invalid
      if (status === "invalid" || status === "error" || result?.data?.invalid === "api_key") {
        setIsApiKeyValid(false);
      }
    } catch (error) {
      console.error("Validation error:", error);
      const invalidField = error?.response?.data?.invalid;
      const status = error?.response?.status;
      if (invalidField === "api_key" || status === 401) {
        setIsApiKeyValid(false);
      } else {
        setToastMessage("Unexpected error — please try again.");
        setIsApiKeyValid(true);
      }
    } finally {
      setIsLoading(false);
      setProgressValue(0);
    }
  };

  return (
    <>
      <Card style={{ display: "flex", width: "100%", minWidth: 0, padding: "var(--space-4)", justifyContent: "space-between",
        alignItems: "center", gap: "var(--space-3)", overflow: "hidden" }}>
        {/* Brevo integration name and logo */}
        <Flex align="center" gap="4" style={{ minWidth: 0, flex: "1 1 auto" }}>
          <Flex height="40px" width="40px" p="2"
            style={{ flexShrink: 0, borderRadius: "var(--space-1)", background: "#FFFFFF", border: "1px solid var(--gray-a6)" }}>
            <img src={require("../../../assets/images/brevo.jpg")} alt="Brevo" style={{ width: "24px" }} />
          </Flex>
          <Flex direction="column" justify="center" align="start" style={{ minWidth: 0 }}>
            <Text size="4" weight="medium" truncate>Brevo</Text>
            <Text size="1" color="gray" truncate>CRM & Email marketing</Text>
          </Flex>
        </Flex>

        {/* Brevo integration status and action button */}
        <Flex align="center" style={{ flexShrink: 0 }}>
          {isBrevoActive() ? (
            <Badge size="1" variant="soft" color="green">
              Connected
            </Badge>
          ) : (
            <Button onClick={handleConnectClick} size="2" variant="soft" color="gray">
              Connect
            </Button>
          )}
        </Flex>
      </Card>

      <AuthBrevoDialog
        isOpen={openDialog}
        onOpenChange={handleOpenChange}
        onCancel={handleCancelAuthDialog}
        onSave={handleSave}
        handleApiKeyChange={handleApiKeyChange}
        isApiKeyValid={isApiKeyValid}
        isLoading={isLoading}
        progressValue={progressValue}
      />

      <BrevoChoiceDialog
        isOpen={choiceDialogOpen}
        onOpenChange={setChoiceDialogOpen}
        data={pendingChoiceData}
        onChoose={(choice) => {
          setChoiceDialogOpen(false);
          handleSave(choice);
        }}
      />

      <PermissionAlertDialog
        isOpen={showPermissionAlert}
        onOpenChange={setShowPermissionAlert}
        message="Only owners can perform this action"
      />
    </>
  );
}
